import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  Home as HomeIcon, 
  BarChart3, 
  Users,
  Key,
  Settings,
  FolderOpen,
  Leaf,
  ChevronDown,
  Settings as SettingsIcon,
  LogOut,
  Activity,
  BookOpen,
  Briefcase,
  Gauge,
  Target,
  Zap,
  FileText
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { projects, selectedProject, setSelectedProject } = useProjects();
  const { logout, user } = useAuth();
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProjectDropdownOpen(false);
      }
      
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigationItems = [
    { 
      name: t('navigation.home'), 
      icon: HomeIcon, 
      path: '/',
      active: location.pathname === '/'
    },
    { 
      name: t('navigation.dashboard'), 
      icon: BarChart3, 
      path: '/dashboard',
      active: location.pathname === '/dashboard'
    },
    { 
      name: t('navigation.portfolioOverview'), 
      icon: Briefcase, 
      path: '/portfolio-overview',
      active: location.pathname === '/portfolio-overview'
    },
    { 
      name: t('navigation.assetCockpit'), 
      icon: Gauge, 
      path: '/asset-cockpit',
      active: location.pathname === '/asset-cockpit'
    },
    { 
      name: t('navigation.strategyDesigner'), 
      icon: Target, 
      path: '/strategy-designer',
      active: location.pathname === '/strategy-designer'
    },
    { 
      name: t('navigation.capacityMarket'), 
      icon: Zap, 
      path: '/capacity-market',
      active: location.pathname === '/capacity-market'
    },
    { 
      name: t('navigation.investorReports'), 
      icon: FileText, 
      path: '/investor-reports',
      active: location.pathname === '/investor-reports'
    },
    { 
      name: t('navigation.emissionActivity'), 
      icon: Activity, 
      path: '/emission-activity',
      active: location.pathname === '/emission-activity'
    },
    { 
      name: t('navigation.emissionFactors'), 
      icon: Leaf, 
      path: '/emission-factors',
      active: location.pathname === '/emission-factors'
    },
    { 
      name: t('navigation.members'), 
      icon: Users, 
      path: '/members',
      active: location.pathname === '/members'
    },
    { 
      name: t('navigation.apiKeys'), 
      icon: Key, 
      path: '/api-keys',
      active: location.pathname === '/api-keys'
    },
    { 
      name: t('navigation.projects'), 
      icon: FolderOpen, 
      path: '/projects',
      active: location.pathname === '/projects'
    }
  ];

  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapsed = () => setCollapsed((c) => !c);

  return (
    <div className={`${collapsed ? 'w-14' : 'w-64'} bg-white shadow-lg h-screen flex flex-col transition-[width] duration-200`}>
      {/* Top bar aligned with Header with interactive burger */}
      <div className="h-12 px-2 lg:px-3 border-b border-gray-200 relative">
        <div className="absolute left-2 lg:left-3 top-1/2 -translate-y-1/2 z-10">
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-pressed={collapsed}
            aria-label="Toggle sidebar"
            className="p-1.5 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
        {!collapsed && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <img
              src={process.env.PUBLIC_URL + '/Caelus_logo_wersja_pozioma_color_rgb.png'}
              alt="Caelus"
              className="h-7 max-w-40 object-contain"
            />
          </div>
        )}
      </div>

      {/* Project Dropdown */}
      <div className={`${collapsed ? 'px-2' : 'px-4'} py-3 border-b border-gray-200`}>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsProjectDropdownOpen((o) => !o)}
            className={`w-full flex items-center ${collapsed ? 'justify-center px-0 py-2' : 'justify-between px-3 py-2'} text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            aria-expanded={isProjectDropdownOpen}
            aria-haspopup="listbox"
          >
            <div className={`flex items-center${collapsed ? '' : ''}`}>
              <FolderOpen className="w-4 h-4 text-gray-500" />
              {!collapsed && (
                <span className="truncate max-w-[140px] ml-2">
                  {selectedProject ? selectedProject.name : t('navigation.selectProject')}
                </span>
              )}
            </div>
            {!collapsed && (
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isProjectDropdownOpen ? 'rotate-180' : ''}`} />
            )}
          </button>

          {isProjectDropdownOpen && (
            <div className={`absolute z-50 ${collapsed ? 'left-full ml-2 w-56' : 'w-full'} mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto`}>
              {projects.length > 0 ? (
                <>
                  {projects.map((project) => (
                                         <button
                       key={project.id}
                       onClick={() => {
                         setSelectedProject(project);
                         setIsProjectDropdownOpen(false);
                       }}
                       className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                         selectedProject && selectedProject.id === project.id
                           ? 'bg-blue-50 text-blue-700'
                           : 'text-gray-700'
                       }`}
                     >
                       <div className="font-medium truncate">{project.name}</div>
                     </button>
                  ))}
                  <div className="border-t border-gray-200">
                    <Link
                      to="/projects"
                      onClick={() => setIsProjectDropdownOpen(false)}
                      className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <SettingsIcon className="w-4 h-4 mr-2" />
                      {t('navigation.manageProjects')}
                    </Link>
                  </div>
                </>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {t('navigation.noProjectsAvailable')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 mt-6 overflow-y-auto">
        <div className={`${collapsed ? 'px-2' : 'px-4'} space-y-2`}>
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center ${collapsed ? 'px-2' : 'px-3'} py-2 rounded-lg transition-colors ${
                  item.active
                    ? 'text-blue-primary bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <IconComponent className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
                {!collapsed && item.name}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Wiki and Settings at bottom above profile */}
      <div className={`${collapsed ? 'px-2' : 'px-4'} pt-3 pb-2 space-y-1`}>
        <Link
          to="/wiki"
          className={`flex items-center ${collapsed ? 'px-2' : 'px-3'} py-2 rounded-lg transition-colors ${
            location.pathname === '/wiki' || location.pathname.startsWith('/wiki/')
              ? 'text-blue-primary bg-blue-50'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <BookOpen className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
          {!collapsed && t('navigation.wiki')}
        </Link>
        <Link
          to="/settings"
          className={`flex items-center ${collapsed ? 'px-2' : 'px-3'} py-2 rounded-lg transition-colors ${
            location.pathname === '/settings'
              ? 'text-blue-primary bg-blue-50'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Settings className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
          {!collapsed && t('navigation.settings')}
        </Link>
      </div>

      {/* User Profile */}
      <div className={`${collapsed ? 'p-3' : 'p-6'} border-t border-gray-200`}>
        {collapsed ? (
          <div className="flex flex-col items-center justify-center gap-2">
            <div
              className="shrink-0 w-8 h-8 bg-blue-primary rounded-full flex items-center justify-center text-white text-sm font-medium"
              title={user?.name || 'User'}
            >
              {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/signin');
              }}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title={t('navigation.logout') || 'Logout'}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center min-w-0 flex-1">
              <div className="shrink-0 w-8 h-8 bg-blue-primary rounded-full flex items-center justify-center text-white text-sm font-medium">
                {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate" title={user?.name || 'User'}>{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500 truncate" title={user?.role || 'User'}>{user?.role || 'User'}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/signin');
              }}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title={t('navigation.logout') || 'Logout'}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
